import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Car, Check, Trash2, Plus } from "lucide-react";
import { format, parseISO, isWithinInterval, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const emptyLoan = (agencyId = "", date = new Date()) => ({
  agency_id: agencyId,
  vehicle: "",
  immatriculation: "",
  client_name: "",
  client_phone: "",
  start_date: format(date, "yyyy-MM-dd"),
  end_date: format(date, "yyyy-MM-dd"),
  notes: "",
  status: "en_cours",
});

export const loansForDay = (loans, day) =>
  loans.filter((l) =>
    isWithinInterval(startOfDay(day), { start: startOfDay(parseISO(l.start_date)), end: startOfDay(parseISO(l.end_date)) })
  );

const loanDays = (loan) => {
  const d = Math.round((parseISO(loan.end_date) - parseISO(loan.start_date)) / 86400000) + 1;
  return d > 1 ? `${d} jours` : "1 jour";
};

export const LoanChip = ({ loan, agency, onClick, compact = false }) => {
  const color = getAgencyColorByAgency(agency);
  const returned = loan.status === "rendu";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`loan-chip-${loan.id}`}
      title={`${loan.vehicle}${loan.immatriculation ? ` (${loan.immatriculation})` : ""} — ${loan.client_name} · ${loanDays(loan)}`}
      className={`w-full text-left rounded px-1.5 py-1 border transition-transform hover:scale-[1.02] hover:z-10 ${returned ? "opacity-50" : ""}`}
      style={{
        background: returned ? "rgba(255,255,255,0.04)" : color.bg,
        borderColor: returned ? "#333" : color.border,
        borderStyle: "dashed",
      }}
    >
      <div className="flex items-center gap-1 min-w-0">
        <KeyRound className="w-3 h-3 shrink-0" style={{ color: returned ? "#777" : color.text }} />
        <span className="text-[11px] font-semibold truncate" style={{ color: returned ? "#999" : color.text }}>
          {loan.vehicle}
        </span>
      </div>
      {!compact && <p className="text-[10px] text-gray-300 truncate">{loan.client_name}</p>}
    </button>
  );
};

export const LoansBanner = ({ days, loans, agencies, onSelect, onAdd }) => {
  if (loans.length === 0) return null;
  return (
    <div className="grid grid-cols-8 border-b" style={{ borderColor: "#1A1A1A", background: "#0D0D0D" }} data-testid="loans-banner">
      <div className="p-2 bg-black flex flex-col items-center justify-center gap-1">
        <KeyRound className="w-4 h-4 text-gray-500" />
        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Prêts</span>
      </div>
      {days.map((day, i) => {
        const dayLoans = loansForDay(loans, day);
        return (
          <div
            key={i}
            className="border-l p-1 space-y-1 min-h-[44px] cursor-pointer hover:bg-white/[0.02]"
            style={{ borderColor: "#1A1A1A" }}
            onClick={() => dayLoans.length === 0 && onAdd(day)}
            data-testid={`loans-day-${format(day, "yyyy-MM-dd")}`}
          >
            {dayLoans.map((loan) => (
              <LoanChip
                key={loan.id}
                loan={loan}
                agency={agencies.find((a) => a.id === loan.agency_id)}
                onClick={(e) => { e.stopPropagation(); onSelect(loan); }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const LoansCompactList = ({ loans, agencies, onSelect, title = "Véhicules de prêt" }) => {
  if (loans.length === 0) return null;
  return (
    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "#1A1A1A", background: "#0D0D0D" }} data-testid="loans-compact-list">
      <p className="text-xs text-gray-500 font-medium flex items-center gap-2">
        <KeyRound className="w-3.5 h-3.5" /> {title.toUpperCase()}
      </p>
      {loans.map((loan) => {
        const agency = agencies.find((a) => a.id === loan.agency_id);
        const color = getAgencyColorByAgency(agency);
        return (
          <button
            key={loan.id}
            type="button"
            onClick={() => onSelect(loan)}
            data-testid={`loan-row-${loan.id}`}
            className="w-full flex items-center justify-between gap-3 rounded px-3 py-2 border border-dashed text-left hover:bg-white/5"
            style={{ borderColor: color.border, background: loan.status === "rendu" ? "transparent" : color.bg }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <AgencyDot color={color} />
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: color.text }}>
                  {loan.vehicle} {loan.immatriculation && <span className="font-mono text-gray-400 text-xs">({loan.immatriculation})</span>}
                </p>
                <p className="text-xs text-gray-400 truncate">{loan.client_name}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-gray-300">
                {format(parseISO(loan.start_date), "d MMM", { locale: fr })} → {format(parseISO(loan.end_date), "d MMM", { locale: fr })}
              </p>
              <Badge variant="outline" className={`text-[10px] ${loan.status === "rendu" ? "border-green-700 text-green-400" : "border-gray-700 text-gray-400"}`}>
                {loan.status === "rendu" ? "Rendu" : loanDays(loan)}
              </Badge>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export const LoanDialog = ({ open, onOpenChange, loan, setLoan, agencies, user, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(loan?.id);
  const canPickAgency = user?.role === "admin" || user?.role === "associe";

  const save = async () => {
    if (!loan.vehicle || !loan.client_name || !loan.agency_id) {
      toast.error("Véhicule, client et agence sont obligatoires");
      return;
    }
    if (loan.end_date < loan.start_date) {
      toast.error("La date de retour doit être après la date de départ");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...loan, immatriculation: loan.immatriculation || null, client_phone: loan.client_phone || null, notes: loan.notes || null };
      if (isEdit) {
        const { id, created_at, created_by, appointment_id, ...rest } = payload;
        await axios.put(`${API}/loans/${id}`, rest);
        toast.success("Prêt modifié");
      } else {
        await axios.post(`${API}/loans`, payload);
        toast.success("Véhicule de prêt ajouté");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await axios.put(`${API}/loans/${loan.id}`, { status });
      toast.success(status === "rendu" ? "Véhicule marqué comme rendu" : "Prêt remis en cours");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer le prêt « ${loan.vehicle} » pour ${loan.client_name} ?`)) return;
    try {
      await axios.delete(`${API}/loans/${loan.id}`);
      toast.success("Prêt supprimé");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!loan) return null;
  const field = (key) => (e) => setLoan({ ...loan, [key]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: "#0A0A0A", borderColor: "#1A1A1A" }} className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="loan-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Bebas Neue, sans-serif", color: "#FEED00", letterSpacing: "0.05em" }} className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            {isEdit ? "VÉHICULE DE PRÊT" : "NOUVEAU VÉHICULE DE PRÊT"}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Affiché dans le bandeau « Prêts » sans occuper de créneau horaire
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {canPickAgency && (
            <div className="space-y-2">
              <Label className="text-gray-300">Agence *</Label>
              <Select value={loan.agency_id} onValueChange={(v) => setLoan({ ...loan, agency_id: v })}>
                <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="loan-agency">
                  <SelectValue placeholder="Choisir une agence" />
                </SelectTrigger>
                <SelectContent style={{ background: "#0A0A0A", borderColor: "#1A1A1A" }}>
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id} className="text-gray-200">
                      <span className="flex items-center gap-2"><AgencyDot color={getAgencyColorByAgency(a)} />{a.city}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300">Véhicule prêté *</Label>
              <Input value={loan.vehicle} onChange={field("vehicle")} placeholder="Ex: Clio 4" className="bg-black border-gray-800 text-white" data-testid="loan-vehicle" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Immatriculation</Label>
              <Input value={loan.immatriculation || ""} onChange={field("immatriculation")} placeholder="AA-123-BB" className="bg-black border-gray-800 text-white font-mono uppercase" data-testid="loan-immat" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300">Client *</Label>
              <Input value={loan.client_name} onChange={field("client_name")} placeholder="Nom du client" className="bg-black border-gray-800 text-white" data-testid="loan-client" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Téléphone</Label>
              <Input value={loan.client_phone || ""} onChange={field("client_phone")} placeholder="06 ..." className="bg-black border-gray-800 text-white" data-testid="loan-phone" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-gray-300">Départ *</Label>
              <Input type="date" value={loan.start_date} onChange={(e) => setLoan({ ...loan, start_date: e.target.value, end_date: loan.end_date < e.target.value ? e.target.value : loan.end_date })} className="bg-black border-gray-800 text-white" data-testid="loan-start" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Retour prévu *</Label>
              <Input type="date" value={loan.end_date} min={loan.start_date} onChange={field("end_date")} className="bg-black border-gray-800 text-white" data-testid="loan-end" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Notes</Label>
            <Input value={loan.notes || ""} onChange={field("notes")} placeholder="Carburant, état, caution..." className="bg-black border-gray-800 text-white" data-testid="loan-notes" />
          </div>

          <Button onClick={save} disabled={saving} className="w-full" style={{ background: "#FEED00", color: "#000" }} data-testid="loan-save">
            {isEdit ? <Check className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {isEdit ? "Enregistrer" : "Ajouter le prêt"}
          </Button>

          {isEdit && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t" style={{ borderColor: "#1A1A1A" }}>
              {loan.status === "rendu" ? (
                <Button variant="outline" size="sm" onClick={() => setStatus("en_cours")} className="border-gray-700 text-gray-300 text-xs" data-testid="loan-reopen">
                  <Car className="w-3 h-3 mr-1" /> Remettre en cours
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setStatus("rendu")} className="border-green-700 text-green-400 hover:bg-green-500/10 text-xs" data-testid="loan-return">
                  <Check className="w-3 h-3 mr-1" /> Véhicule rendu
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={remove} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs" data-testid="loan-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Supprimer
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
